import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { StatCard } from "../../../ui";

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return `${value ?? 0} Rs`;
  return `Rs ${n.toLocaleString("en-US")}`;
}

function remainingDays(endDate) {
  if (!endDate) return null;
  return dayjs(endDate).startOf("day").diff(dayjs().startOf("day"), "day");
}

export default function SubscriptionStatsModal({
  filtered_ledgers,
  open,
  setOpen,
}) {
  const list = filtered_ledgers || [];
  const totalSubscriptions = list.length;
  const activeSubscriptions = list.filter((sub) => sub.is_active).length;
  const inactiveSubscriptions = totalSubscriptions - activeSubscriptions;
  const expiringSoon = list.filter((sub) => {
    if (!sub.is_active) return false;
    const days = remainingDays(sub.end_date);
    return days !== null && days >= 0 && days <= 7;
  }).length;
  const outstanding = list.reduce(
    (sum, sub) => sum + (Number(sub?.member_id?.balance) || 0),
    0
  );

  const typeCounts = list.reduce((acc, sub) => {
    const key = (sub.renewal_frequency || "Unspecified").trim() || "Unspecified";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const handleDownload = () => {
    const stats = [
      ["Total Subscriptions", totalSubscriptions],
      ["Active Subscriptions", activeSubscriptions],
      ["Expired Subscriptions", inactiveSubscriptions],
      ["Expiring within 7 days", expiringSoon],
      ["Outstanding balance (Rs)", outstanding],
      [],
      ["Cycle type", "Count"],
      ...Object.entries(typeCounts),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet([
      ["Subscription Stats", ""],
      ...stats,
    ]);
    worksheet["!cols"] = [{ width: 32 }, { width: 20 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Subscription Stats");
    XLSX.writeFile(workbook, "subscription_stats.xlsx");
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          backgroundColor: "var(--gx-surface)",
          overflow: "hidden",
        },
      }}
    >
      <div className="h-1 bg-gradient-to-r from-[#D71920] to-[#F7931E]" />
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-2">
        <div>
          <h2 className="text-xl font-bold text-gx-ink tracking-tight">
            Subscription statistics
          </h2>
          <p className="text-sm text-gx-ink-soft mt-1">
            Snapshot of the roster currently in view.
          </p>
        </div>
        <IconButton
          aria-label="Close statistics"
          onClick={() => setOpen(false)}
          className="min-h-[44px] min-w-[44px]"
        >
          <CloseIcon />
        </IconButton>
      </div>
      <DialogContent className="px-5 pb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          <StatCard
            featured
            label="In view"
            value={totalSubscriptions.toLocaleString("en-US")}
            icon={<GroupsOutlinedIcon fontSize="small" />}
            accent="#022A3A"
            hint="Matching current filters"
          />
          <StatCard
            label="Active"
            value={activeSubscriptions.toLocaleString("en-US")}
            icon={<CheckCircleOutlineOutlinedIcon fontSize="small" />}
            accent="#047857"
            hint="Live memberships"
          />
          <StatCard
            label="Expired"
            value={inactiveSubscriptions.toLocaleString("en-US")}
            icon={<EventBusyIcon fontSize="small" />}
            accent="#D71920"
            hint="Need renewal"
          />
          <StatCard
            label="Expiring soon"
            value={expiringSoon.toLocaleString("en-US")}
            icon={<AccessTimeIcon fontSize="small" />}
            accent="#F7931E"
            hint="Ends within 7 days"
          />
          <StatCard
            label="Outstanding"
            value={formatMoney(outstanding)}
            icon={<AccountBalanceWalletIcon fontSize="small" />}
            accent="#D71920"
            hint="Member balances in view"
          />
        </div>

        {Object.keys(typeCounts).length > 0 ? (
          <div className="mt-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft mb-2">
              By cycle
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(typeCounts).map(([label, count]) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full bg-gx-navy/10 text-gx-navy ring-1 ring-gx-navy/15 px-3 py-1.5 text-xs font-semibold"
                >
                  <span className="whitespace-nowrap truncate max-w-[10rem]">{label}</span>
                  <span className="tabular-nums">{count}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-6 flex justify-end">
          <button type="button" className="gx-cta min-h-[44px] gap-2" onClick={handleDownload}>
            <DownloadIcon fontSize="small" aria-hidden="true" />
            Download stats
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
